/**
 * Manual Table Parser
 * Parses pasted table content with proper column alignment
 * Detects headers automatically and preserves empty columns
 */

/**
 * Parse a table from pasted text content
 * @param {string} text - The pasted table text
 * @param {Object} options - Configuration options
 * @param {number} options.headerLines - Number of lines to treat as headers (default: auto-detect)
 * @param {string[]} options.columnKeys - Array of column keys/names to map data to (optional)
 * @param {boolean} options.preserveEmptyColumns - Whether to preserve empty columns (default: true)
 * @param {RegExp} options.rowStartRegex - Regex to identify start of data rows (default: /^\d+\b/)
 * @returns {Object} Object with headers array and data array
 */
export function parseManualTable(text, options = {}) {
  const {
    headerLines = null, // null = auto-detect
    columnKeys = null, // null = use detected headers
    preserveEmptyColumns = true,
    rowStartRegex = /^\d+\b/, // Default: rows starting with numbers
  } = options;

  if (!text || typeof text !== "string") {
    return { headers: [], data: [], tableTitle: null };
  }

  // Split into lines and clean
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

  if (lines.length === 0) {
    return { headers: [], data: [], tableTitle: null };
  }

  // Detect table title/heading (usually first line that doesn't contain tabs and is all caps or title case)
  let tableTitle = null;
  let startIndex = 0;
  
  // Check if first line looks like a title (no tabs, contains words like "HISTORY", "TABLE", etc.)
  if (lines.length > 0) {
    const firstLine = lines[0];
    const hasTabsInFirstLine = firstLine.includes("\t");
    const looksLikeTitle = 
      !hasTabsInFirstLine && 
      (firstLine.toUpperCase() === firstLine || 
       /^(HISTORY|TABLE|LIST|DATA|RECORDS|DETAILS)\s+(OF|FOR)/i.test(firstLine) ||
       firstLine.split(/\s+/).length <= 5); // Short line, likely a title
    
    if (looksLikeTitle && lines.length > 1) {
      tableTitle = firstLine;
      startIndex = 1; // Skip the title line
    }
  }

  // Use remaining lines for parsing
  const tableLines = lines.slice(startIndex);

  if (tableLines.length === 0) {
    return { headers: [], data: [], tableTitle };
  }

  // Detect if table uses tabs or spaces
  const hasTabs = tableLines.some((line) => line.includes("\t"));
  const delimiter = hasTabs ? "\t" : null; // null = space-aligned

  // Function to split a line into columns
  const splitColumns = (line) => {
    if (delimiter === "\t") {
      // Tab-separated: split by single tabs to preserve empty columns
      // Important: Use split('\t') not split(/\t+/) to preserve empty strings
      const parts = line.split('\t');
      return parts.map((col) => col.trim());
    } else {
      // Space-aligned: split by multiple spaces (2+)
      // This preserves empty columns when there are multiple spaces
      const parts = line.split(/ {2,}/);
      return parts.map((col) => col.trim());
    }
  };

  // Auto-detect header lines if not specified
  let actualHeaderLines = headerLines;
  if (actualHeaderLines === null) {
    // For tab-separated tables, first line is usually the header
    if (hasTabs && tableLines.length > 0) {
      const firstLine = tableLines[0];
      // Check if first line looks like a header (contains common header words or doesn't start with number)
      const looksLikeHeader = 
        !rowStartRegex.test(firstLine) || 
        /^(ID|REGISTRATION|SR|SERIAL|CHALLAN|INSURANCE|COMPANY|TYPE|FROM|UPTO|DATE|NO|NUMBER|OFFICE|CREATED|UPDATED|ACTIONS|RECEIPT|TAX|MODE|BREAKUP|PENALTY|GRN)/i.test(firstLine);
      
      if (looksLikeHeader) {
        actualHeaderLines = 1;
      } else {
        // First line is data, no headers
        actualHeaderLines = 0;
      }
    } else {
      // For space-aligned tables, find first line that matches rowStartRegex
      for (let i = 0; i < tableLines.length; i++) {
        if (rowStartRegex.test(tableLines[i])) {
          actualHeaderLines = i;
          break;
        }
      }
      // If no match found, assume first 1-3 lines are headers
      if (actualHeaderLines === null) {
        actualHeaderLines = Math.min(3, Math.floor(tableLines.length / 2));
      }
    }
  }

  // Extract headers
  const headerLinesArray = tableLines.slice(0, actualHeaderLines);
  const dataLines = tableLines.slice(actualHeaderLines);

  // Process headers - combine multi-line headers
  let headers = [];
  if (headerLinesArray.length > 0) {
    // For tab-separated tables, use the first header line's column count as reference
    // This ensures we preserve all columns including empty ones
    const firstHeaderLine = headerLinesArray[0];
    const firstHeaderCols = splitColumns(firstHeaderLine);
    const maxCols = Math.max(
      firstHeaderCols.length,
      ...headerLinesArray.map((line) => splitColumns(line).length)
    );

    // Initialize headers array with the correct number of columns
    headers = Array(maxCols).fill("");

    // Combine header lines
    headerLinesArray.forEach((line) => {
      const cols = splitColumns(line);
      cols.forEach((col, idx) => {
        if (idx < headers.length) {
          if (headers[idx]) {
            headers[idx] += " " + col;
          } else {
            headers[idx] = col;
          }
        }
      });
    });

    // Clean up headers - preserve empty header names as "Column_N" where N is index
    headers = headers.map((h, idx) => {
      const trimmed = h.trim();
      return trimmed || `Column_${idx + 1}`;
    });
  }

  // Use provided column keys if available, otherwise use detected headers
  const keys = columnKeys || headers;

  if (keys.length === 0) {
    return { headers: [], data: [] };
  }

  // Parse data rows
  const data = [];
  
  // For tab-separated tables, process each line as a complete row
  if (hasTabs) {
    for (let i = 0; i < dataLines.length; i++) {
      const line = dataLines[i];
      let columns = splitColumns(line);

      // If row has more columns than headers, skip leading numeric-only cells (stray index/ID) so columns align
      while (columns.length > keys.length && /^\d+$/.test((columns[0] || "").trim())) {
        columns = columns.slice(1);
      }

      // Ensure we have the same number of columns as headers
      // Pad with empty strings if needed
      while (columns.length < keys.length) {
        columns.push("");
      }
      
      // Create row object
      const row = {};
      let validRow = false;

      keys.forEach((key, idx) => {
        const value = idx < columns.length ? (columns[idx] || "") : "";
        row[key] = value;
        if (value && value.trim()) {
          validRow = true;
        }
      });

      // Add row if it has at least some data (at least one non-empty column)
      if (validRow || columns.length > 0) {
        data.push(row);
      }
    }
  } else {
    // For space-aligned tables, use the original multi-line logic
    let currentRow = null;
    let currentRowLines = [];

    for (let i = 0; i < dataLines.length; i++) {
      const line = dataLines[i];

      // Check if this line starts a new row
      if (rowStartRegex.test(line)) {
        // Save previous row if exists
        if (currentRowLines.length > 0 && currentRow) {
          data.push(currentRow);
        }

        // Start new row
        currentRowLines = [line];
        currentRow = null;
      } else if (currentRowLines.length > 0) {
        // Continuation of current row (multi-line row)
        currentRowLines.push(line);
      } else {
        // Skip lines that don't match row start and aren't continuations
        continue;
      }

      // Try to parse current row
      if (currentRowLines.length > 0) {
        const rowText = currentRowLines.join(" ");
        const columns = splitColumns(rowText);

        // Ensure we have the same number of columns as headers
        while (columns.length < keys.length) {
          columns.push("");
        }

        // Create row object
        const row = {};
        let validRow = false;

        keys.forEach((key, idx) => {
          const value = idx < columns.length ? (columns[idx] || "") : "";
          row[key] = value;
          if (value && value.trim()) {
            validRow = true;
          }
        });

        // Only add row if it has at least some data
        if (validRow) {
          currentRow = row;
        }
      }
    }

    // Don't forget the last row
    if (currentRowLines.length > 0 && currentRow) {
      data.push(currentRow);
    }
  }

  return {
    headers: headers,
    data: data,
    detectedHeaderLines: actualHeaderLines,
    tableTitle: tableTitle,
  };
}

/**
 * Parse table and return as array of objects with specified keys
 * @param {string} text - The pasted table text
 * @param {string[]} keys - Array of column keys to map to
 * @param {Object} options - Additional options
 * @returns {Array} Array of objects with the specified keys
 */
export function parseTableToObjects(text, keys, options = {}) {
  const result = parseManualTable(text, {
    ...options,
    columnKeys: keys,
  });
  return result.data;
}

/**
 * Enhanced parser that handles various table formats
 * Supports both tab-separated and space-aligned tables
 * @param {string} text - The pasted table text
 * @param {string[]} keys - Column keys
 * @param {Object} options - Parser options
 * @returns {Array} Parsed data as array of objects
 */
export function parseTableAdvanced(text, keys, options = {}) {
  const {
    headerLines = null,
    rowStartRegex = /^\d+\b/,
    insertEmptyAt = [], // Array of indices where to insert empty strings if column is missing
    minColumns = 0, // Minimum number of columns required
  } = options;

  if (!text || !Array.isArray(keys) || keys.length === 0) {
    return [];
  }

  const result = parseManualTable(text, {
    headerLines,
    columnKeys: keys,
    preserveEmptyColumns: true,
    rowStartRegex,
  });

  const data = result.data.map((row) => {
    const parts = keys.map((key) => row[key] ?? "");

    // Handle missing columns by inserting empty strings at specified indices
    if (insertEmptyAt.length > 0 && parts.length === keys.length - insertEmptyAt.length) {
      const sorted = [...insertEmptyAt].sort((a, b) => a - b);
      for (const idx of sorted) {
        if (idx >= 0 && idx <= parts.length) {
          parts.splice(idx, 0, "");
        }
      }
    }

    // Ensure we have the right number of columns
    while (parts.length < keys.length) {
      parts.push("");
    }

    // Create object
    const obj = {};
    keys.forEach((key, i) => {
      obj[key] = parts[i] ?? "";
    });

    return obj;
  });

  // Filter out rows that don't meet minimum column requirement
  if (minColumns > 0) {
    return data.filter((row) => {
      const nonEmptyCount = keys.filter((key) => row[key] && row[key].trim()).length;
      return nonEmptyCount >= minColumns;
    });
  }

  return data;
}

/**
 * Quick parse function with sensible defaults
 * @param {string} text - Table text
 * @param {string[]} keys - Column keys
 * @returns {Array} Parsed data
 */
export function quickParseTable(text, keys) {
  return parseTableAdvanced(text, keys, {
    rowStartRegex: /^\d+\b/,
    minColumns: 2, // At least 2 columns must have data
  });
}

/**
 * Normalize column names from headers to database format
 * Converts "REGISTRATION NO" to "REGISTRATION_NO" or keeps as provided
 * @param {string[]} headers - Array of header strings
 * @returns {string[]} Normalized column names
 */
export function normalizeColumnNames(headers) {
  return headers.map((header) => {
    // Convert to uppercase and replace spaces/special chars with underscores
    return header
      .toUpperCase()
      .trim()
      .replace(/\s+/g, "_")
      .replace(/[^A-Z0-9_]/g, "_")
      .replace(/_+/g, "_")
      .replace(/^_|_$/g, "");
  });
}

/**
 * Convert table title to database table name format
 * @param {string} title - Table title like "HISTORY OF ROAD TAX"
 * @returns {string} Database table name like "history_of_road_tax"
 */
export function titleToTableName(title) {
  if (!title) return null;
  return title
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

/**
 * Format parsed table result as JSON with proper database structure
 * @param {Object} result - Parsed table result from parseManualTable
 * @returns {Object} Formatted JSON object ready for database
 */
export function formatTableResultAsJSON(result) {
  if (!result || !result.data || result.data.length === 0) {
    return result?.tableTitle ? { [titleToTableName(result.tableTitle)]: [] } : {};
  }

  const tableName = result.tableTitle ? titleToTableName(result.tableTitle) : "table_data";
  
  return {
    [tableName]: result.data,
    ...(result.tableTitle && { table_title: result.tableTitle }),
  };
}

/**
 * Parse "History of Challan" tab-separated format with multi-line rows (row starts with digit + tab).
 * Headers: Serial no, Challan No, Offense Details Name/Act, Challan Place, Challan Date/Time, State, RTO, Accussed Name, Amount, Challan Status, Payment source, Payment Date, Receipt Number, Challan Print
 * @param {string} text - The pasted table text
 * @returns {Object|null} { headers, data, tableTitle } or null
 */
export function parseChallanTable(text) {
  if (!text || typeof text !== "string") return null;
  if (!/History\s+of\s+Challan/i.test(text)) return null;
  const lines = text.split(/\r?\n/).map((l) => l.trim());
  let start = 0;
  let tableTitle = "History of Challan";
  while (start < lines.length && /^(\d+\.\s*)?History\s+of\s+/i.test(lines[start])) {
    if (/Challan/i.test(lines[start])) tableTitle = lines[start].replace(/^\d+\.\s*/, "").trim();
    start++;
  }
  const rest = lines.filter((l) => l.length > 0).slice(start);
  if (rest.length < 2) return null;

  const rowStartRe = /^\d+\t/;
  let headerLine = null;
  let firstDataIdx = -1;
  for (let i = 0; i < rest.length; i++) {
    if (rest[i].includes("\t") && /Serial\s*no|Challan\s*No/i.test(rest[i]) && !rowStartRe.test(rest[i])) {
      headerLine = rest[i];
      break;
    }
  }
  for (let i = 0; i < rest.length; i++) {
    if (rowStartRe.test(rest[i])) {
      firstDataIdx = i;
      break;
    }
  }
  if (!headerLine || firstDataIdx < 0) return null;

  const headers = headerLine.split("\t").map((h) => h.trim() || "Column");
  const numCols = Math.max(headers.length, 14);
  while (headers.length < numCols) headers.push(`Column_${headers.length + 1}`);

  // Last 11 columns are fixed: Challan Place, Challan Date/Time, State, RTO, Accussed Name, Amount, Challan Status, Payment source, Payment Date, Receipt Number, Challan Print
  const fixedColsFromEnd = 11;

  const rowStarts = rest.map((line, i) => (rowStartRe.test(line) ? i : -1)).filter((i) => i >= 0);
  const data = [];
  for (let r = 0; r < rowStarts.length; r++) {
    const from = rowStarts[r];
    const to = r + 1 < rowStarts.length ? rowStarts[r + 1] : rest.length;
    const block = rest.slice(from, to).join("\n");
    const parts = block.split("\t").map((p) => p.trim());
    const row = {};
    if (parts.length > numCols && parts.length > 2 + fixedColsFromEnd) {
      // Offense column may contain embedded tabs; keep last 11 columns aligned
      const offenseParts = parts.slice(2, parts.length - fixedColsFromEnd);
      for (let c = 0; c < numCols; c++) {
        const label = headers[c];
        if (c === 0) row[label] = parts[0] ?? "";
        else if (c === 1) row[label] = parts[1] ?? "";
        else if (c === 2) row[label] = offenseParts.join(" ").trim();
        else row[label] = parts[parts.length - fixedColsFromEnd + (c - 3)] ?? "";
      }
    } else {
      for (let c = 0; c < numCols; c++) {
        const label = headers[c];
        row[label] = (parts[c] ?? "").trim();
      }
    }
    data.push(row);
  }
  if (data.length === 0) return null;
  return { headers: headers.slice(0, numCols), data, tableTitle, detectedHeaderLines: 1 };
}

/**
 * Detect if a line looks like data (receipt number, date, number) - used for vertical format
 */
function isVerticalDataLine(line) {
  const s = (line || "").trim();
  if (!s) return false;
  // Receipt / ID pattern: long alphanumeric (e.g. HR251117C4511608)
  if (/^[A-Z0-9]{10,}$/i.test(s)) return true;
  // Date: DD-MMM-YYYY or DD-MMM-YYYY HH:MM:SS AM/PM
  if (/^\d{1,2}-[A-Za-z]{3}-\d{4}/.test(s)) return true;
  // Number (integer or decimal)
  if (/^\d+\.?\d*$/.test(s)) return true;
  return false;
}

/**
 * Parse "vertical" table format: title lines, then one header per line (including blank lines),
 * then one value per line in row-major order. Blank lines in the header block count as a column.
 * E.g. "History of Paid Road Tax" then "Receipt No", "Tax From", ..., "Office", "", "Tax", ...
 * then HR251117C4511608, 01-Dec-2025, ...
 * @param {string} text - The pasted table text
 * @returns {Object|null} { headers, data, tableTitle } or null if not vertical format
 */
export function parseVerticalTable(text) {
  if (!text || typeof text !== "string") return null;
  // Keep empty lines so header count matches data columns (e.g. blank line between "Office" and "Tax")
  const lines = text.split(/\r?\n/).map((l) => l.trim());
  const nonEmptyCount = lines.filter((l) => l.length > 0).length;
  if (nonEmptyCount < 5) return null;

  let tableTitle = null;
  let start = 0;
  // Skip title lines: "5. History of Paid Road Tax", "History of Paid Road Tax" (and blank lines after title)
  while (start < lines.length) {
    const line = lines[start];
    if (/^(\d+\.\s*)?History\s+of\s+/i.test(line)) {
      if (!tableTitle) tableTitle = line.replace(/^\d+\.\s*/, "").trim();
      start++;
    } else if (line.length === 0 && start > 0 && /^(\d+\.\s*)?History\s+of\s+/i.test(lines[start - 1])) {
      start++;
    } else {
      break;
    }
  }
  const rest = lines.slice(start);
  if (rest.length < 3) return null;

  // First line that looks like data = end of header block
  const headerEnd = rest.findIndex((line) => line.length > 0 && isVerticalDataLine(line));
  if (headerEnd <= 0 || headerEnd > 50) return null;

  let headerLinesRaw = rest.slice(0, headerEnd);
  while (headerLinesRaw.length > 0 && headerLinesRaw[headerLinesRaw.length - 1].length === 0) {
    headerLinesRaw = headerLinesRaw.slice(0, -1);
  }
  const numDataCols = headerLinesRaw.length;
  const headers = headerLinesRaw.filter((h) => h.length > 0);
  const dataLines = rest.slice(headerEnd);
  if (dataLines.length < numDataCols) return null;

  const data = [];
  let headersUsed = null;
  for (let r = 0; r + numDataCols <= dataLines.length; r += numDataCols) {
    const row = {};
    const keysThisRow = [];
    for (let c = 0; c < numDataCols; c++) {
      let label = headerLinesRaw[c];
      if (label.length > 0) {
        let key = label;
        if (row[key] !== undefined) {
          let n = 2;
          while (row[`${label}_${n}`] !== undefined) n++;
          key = `${label}_${n}`;
        }
        row[key] = dataLines[r + c] ?? "";
        keysThisRow.push(key);
      }
    }
    if (headersUsed === null) headersUsed = keysThisRow;
    data.push(row);
  }
  return { headers: headersUsed || headerLinesRaw.filter((h) => h.length > 0), data, tableTitle, detectedHeaderLines: headerLinesRaw.length };
}

/**
 * Parse table and automatically normalize column names
 * @param {string} text - The pasted table text
 * @param {Object} options - Configuration options
 * @returns {Object} Object with normalized headers and data array
 */
export function parseTableWithNormalizedHeaders(text, options = {}) {
  // Try Challan format (tab-separated, multi-line rows starting with digit+tab)
  if (/History\s+of\s+Challan/i.test(text)) {
    const challanResult = parseChallanTable(text);
    if (challanResult && challanResult.headers.length > 0 && challanResult.data.length > 0) {
      const normalizedHeaders = normalizeColumnNames(challanResult.headers);
      const normalizedData = challanResult.data.map((row) => {
        const newRow = {};
        challanResult.headers.forEach((originalHeader, idx) => {
          const normalizedKey = normalizedHeaders[idx];
          const value = row[originalHeader] ?? row[normalizedKey] ?? "";
          newRow[normalizedKey] = value;
        });
        return newRow;
      });
      return {
        headers: normalizedHeaders,
        originalHeaders: challanResult.headers,
        data: normalizedData,
        detectedHeaderLines: challanResult.detectedHeaderLines,
        tableTitle: challanResult.tableTitle,
      };
    }
  }

  // Try vertical format (one header per line, then row-major values)
  const verticalResult = parseVerticalTable(text);
  if (verticalResult && verticalResult.headers.length > 0 && verticalResult.data.length > 0) {
    const normalizedHeaders = normalizeColumnNames(verticalResult.headers);
    const normalizedData = verticalResult.data.map((row) => {
      const newRow = {};
      verticalResult.headers.forEach((originalHeader, idx) => {
        const normalizedKey = normalizedHeaders[idx];
        const value = row[originalHeader] ?? row[normalizedKey] ?? "";
        newRow[normalizedKey] = value;
      });
      return newRow;
    });
    return {
      headers: normalizedHeaders,
      originalHeaders: verticalResult.headers,
      data: normalizedData,
      detectedHeaderLines: verticalResult.detectedHeaderLines,
      tableTitle: verticalResult.tableTitle,
    };
  }

  // Fall back to tab/space table
  const result = parseManualTable(text, {
    ...options,
    columnKeys: null, // Use detected headers first
  });

  if (result.headers.length > 0) {
    const normalizedHeaders = normalizeColumnNames(result.headers);
    
    // Map data rows to use normalized headers
    const normalizedData = result.data.map((row) => {
      const newRow = {};
      result.headers.forEach((originalHeader, idx) => {
        const normalizedKey = normalizedHeaders[idx];
        // Get value from row using original header as key
        // If row uses original header, use it; otherwise try normalized key
        const value = row[originalHeader] ?? row[normalizedKey] ?? "";
        newRow[normalizedKey] = value;
      });
      return newRow;
    });

    return {
      headers: normalizedHeaders,
      originalHeaders: result.headers,
      data: normalizedData,
      detectedHeaderLines: result.detectedHeaderLines,
      tableTitle: result.tableTitle,
    };
  }

  return result;
}
